/**
 * Hawker Commissary Assignment service
 */
import { Injectable, Injector } from '@angular/core';
import { ColDef, ValueGetterParams } from 'ag-grid-community';

import { BaseService } from '../base';
import {
  IEntityConfig,
  ENTITY_TYPES,
  CELL_RENDERER,
  IXVIListColumn,
  ListColumn,
  IBaseEntityType,
  GRID_ROW_SELECTION,
  CELL_FILTER,
  GridRowClass,
  IApiListResponse,
  SupportedAppCodes,
} from '@shared/types';
import {
  filter,
  get,
  gt,
  has,
  includes,
  isEmpty,
  set,
  stubFalse,
} from 'lodash';
import { I18n } from '@app/i18n/strings-keys';
import { ComponentSelectorResult } from 'ag-grid-community/dist/lib/components/framework/userComponentFactory';
import { isLeaf } from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import {
  EVENT_STATUS_VALUE,
  EVENT_STATUS_LABEL_TEXT_MAPPING,
} from '@shared/types/enums/event-status.enum';
import { XprtDataService } from '@shared/services/xprt-data/xprt-data.service';
import { BehaviorSubject, rx } from '@shared/rx';
import { getListGridRowCellClass } from '@shared/helpers/ag-grid-helpers';
import { FormControl, FormGroup } from '@angular/forms';
import { INTERNAL_GRID_ROW_ID } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component.constants';
import {
  eventsFilterValueGetter,
  getEventsObs,
  convertEventsToTree,
} from './hawker-commissary-assignment.utils';
import { MAX_PAGE_SIZE } from '@config/global-constants';
import { size } from 'lodash/fp';

const entityType: ENTITY_TYPES = ENTITY_TYPES.HAWKER_COMMISSARY_ASSIGNMENT;
const config: IEntityConfig = {
  ENTITY_TYPE: entityType,
  isMasterListAvailable: true,
  entityNameToCheckPermissions: ENTITY_TYPES.HAWKER_CONFIG,
  buttons: null,
  apiEndpoint: entityType,
  uiRoute: entityType,
  list: {
    dependentEntities: [
      {
        entityType: ENTITY_TYPES.EVENTS,
        query: {
          filter_by_assigned_sites: false,
          select: ['name', 'status', 'id', 'available_sites', 'event_days'],
          include_available_sites: true,
          available_sites_select: 'id,name,business_type,i18n',
          filter: `status eq in(${EVENT_STATUS_VALUE.Open},${EVENT_STATUS_VALUE.Pending})`,
        },
      },
      {
        entityKey: 'usersInfo',
        request$: ({ injector: requestInjector }) =>
          requestInjector
            .get(XprtDataService)
            .getUsersInfoByFilter(
              {},
              {
                top: MAX_PAGE_SIZE,
                filter_by_assigned_sites: false,
                include_sites: true,
              },
            )
            .pipe(
              rx.take(1),
              rx.map((items) => ({
                items,
                total: get(items, 'length', 0),
              })),
            ),
      },
      {
        entityKey: 'rolesData',
        request$: ({ injector: requestInjector }) =>
          requestInjector
            .get(XprtDataService)
            .fetchRoleDataByCompanyId({
              filter: `permissions/any(p:(p/app_code eq '${SupportedAppCodes.XVI}' and p/name eq 'VenuesInventory.HawkerSession.Add' and p/value eq true)) and permissions/any(p:(p/app_code eq '${SupportedAppCodes.XVI}' and p/name eq 'VenuesInventory.HawkerSession.Manage' and p/value eq true))`,
            })
            .pipe(
              rx.take(1),
              rx.map((items: IBaseEntityType[]) => ({
                items,
                total: get(items, 'length', 0),
              })),
            ),
      },
    ],
    userGridOptions: {
      enableServerSideFilter: false,
      enableServerSideSorting: false,
      rowSelection: GRID_ROW_SELECTION.SINGLE,
      suppressRowClickSelection: true,
      deltaRowDataMode: false,
      // for tree mode only expanded some rows
      getRowNodeId: (data): string => {
        return get(data, 'event_id', get(data, INTERNAL_GRID_ROW_ID, ''));
      },
      rowClassRules: {
        ...getListGridRowCellClass(),
        'xvi-grid-warn-row': (params): boolean => {
          // Highlight rows where manager is no longer available
          // Works for both parent event rows and child site rows
          return get(params, 'data.hasWarning', false);
        },
        [GridRowClass.ERROR_ROW]: (params): boolean => {
          if (isLeaf(params)) {
            return false;
          }

          const formGroup: FormGroup = get(
            params,
            ['context', 'formGroup'],
            null,
          );

          if (!formGroup?.hasError('duplicateAssignments')) {
            return false;
          }

          const eventId = isLeaf(params)
            ? get(params, 'node.parent.data.event_id')
            : get(params, 'data.event_id');

          return has(
            get(formGroup, ['errors', 'duplicateAssignments', 'data']),
            eventId,
          );
        },
      },
    },
    gridOptions: {
      multiselect: false,
      treeViewMode: true,
      treeTotalLevel: 0,
      keepNodeOwnExpanded: true,
      enableDefaultExpandFn: true,
      keepTreeStructureWhenSave: true,
      treeChildrenPropertyName: 'children',
      disableColumnSelector: true,
      showViewSelector: false,
      showSearch: true,
      showPagination: false,
      hiddenGridHeaderCheckbox: true,
    },
    partialUpdateIdentifyPath: 'event_id',
    isHiddenRefreshButton: true,
    documentQuery: {
      notNeedActive: true,
      select: ['event_id', 'commissary_assignments'],
      orderby: 'event_date asc',
    },
    listItemsLabel: I18n.Forms.GridMessages.EventsBrackets,
    shouldUpdateNodes: stubFalse,
    warningMessage$: new BehaviorSubject(null),
    afterFetchListData: (items, options) => {
      const dependentData = options?.dependentEntitiesData;

      const events: IBaseEntityType[] = get(
        dependentData,
        [ENTITY_TYPES.EVENTS, 'items'],
        [],
      );

      const users: IBaseEntityType[] = get(
        dependentData,
        ['usersInfo', 'items'],
        [],
      );

      const roles: IBaseEntityType[] = get(
        dependentData,
        ['rolesData', 'items'],
        [],
      );

      return convertEventsToTree(events, users, roles, items);
    },
  },
};

/**
 * Hawker Commissary Assignment service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class HawkerCommissaryAssignmentService extends BaseService {
  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = config.ENTITY_TYPE;

  public quickFilterFields: string[] = ['name'];

  public listProjection: string[] = ['name'];

  public headerNames: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      filter: false,
    },
  ];

  public constructor(public injector: Injector) {
    super(injector);
    this.config = HawkerCommissaryAssignmentService.config;

    const hasManagePermission = this.permissionService.canManage(
      this.config.entityNameToCheckPermissions,
    );

    set(this.config, 'list.columns', <IXVIListColumn[]>[
      {
        path: 'event_name',
        title: I18n.EntityNames.Event,
        cellRenderer: CELL_RENDERER.ag_group_cell_renderer,
        cellRendererParams: {
          noChildrenTrailTips:
            I18n.Forms.HawkerCommissaryAssignmentForm.NoCommissary,
        },
        width: 360,
        showRowGroup: true,
        defaultView: true,
        sortable: true,
        filterType: CELL_FILTER.entity,
        filterParams: {
          quickFilter: true,
          values: getEventsObs({ dependentData$: this.dependentData$ }),
          suppressSelectAllControl: true,
          filterValueGetter: eventsFilterValueGetter,
        },
      },
      {
        path: 'event_date',
        title: I18n.Common.Date,
        defaultView: true,
        sortable: true,
        width: 140,
        filterType: CELL_FILTER.dateLocale,
        filterParams: {
          suppressUIFiltering: false,
          matchParentForTree: true,
        },
        valueFormatter: (params: ValueGetterParams): string => {
          const eventDate = get(params, 'data.event_date', '');

          return isLeaf(params) || isEmpty(eventDate)
            ? ''
            : this.companyService.convertUTCDateWithTimeZone(eventDate, true);
        },
      },
      {
        path: 'event_status',
        title: I18n.Common.Status,
        defaultView: true,
        sortable: true,
        width: 120,
        valueGetter: (params: ValueGetterParams): string => {
          return isLeaf(params) ? '' : params.data.event_status;
        },
        cellRenderer: CELL_RENDERER.prepared_string_renderer,
        cellRendererParams: {
          valueToEnumMap: EVENT_STATUS_LABEL_TEXT_MAPPING,
        },
      },
      {
        path: 'site_name',
        title: I18n.EntityNames.Site,
        defaultView: true,
        valueGetter: (params: ValueGetterParams): string => {
          return isLeaf(params) ? params.data.site_name : '';
        },
      },
      {
        path: 'user_id',
        title: I18n.Forms.HawkerCommissaryAssignmentForm.HawkerManager,
        defaultView: true,
        editable: hasManagePermission,
        minWidth: 180,
        width: 180,
        cellClass: 'cell-overflow-visible-without-hover',
        cellRendererSelector: (params): ComponentSelectorResult | null => {
          if (!isLeaf(params)) {
            return null;
          }

          const component = CELL_RENDERER.custom_select_renderer;

          const renderParams: ListColumn['cellRendererParams'] = {
            options: get(params, 'data.managerOptions', []),
            dropdownShownItemCount: 5,
            placeholder: gt(size(get(params, 'data.managerOptions', [])), 0)
              ? I18n.Forms.GridForm.NotAssigned
              : I18n.Forms.HawkerCommissaryAssignmentForm.NoAvailableManagers,
            disabled: !hasManagePermission,
            value: get(params, 'data.user_id'),
            searchable: true,
            emptyOptionConfig: {
              text: I18n.Forms.GridForm.NotAssigned,
              value: '',
            },
            isErrorFn: (errorParams) => {
              const curUserId = get(errorParams, 'data.user_id');

              if (isEmpty(curUserId)) {
                return false;
              }

              const formGroup: FormGroup = get(
                errorParams,
                ['context', 'formGroup'],
                null,
              );

              if (!formGroup?.hasError('duplicateAssignments')) {
                return false;
              }

              const curEventId = get(errorParams, 'node.parent.data.event_id');

              const duplicateSites = get(
                formGroup,
                [
                  'errors',
                  'duplicateAssignments',
                  'data',
                  curEventId,
                  curUserId,
                ],
                null,
              );

              return includes(duplicateSites, get(params, 'data.site_id'));
            },
          };

          return {
            component,
            params: renderParams,
          };
        },
      },
    ]);
  }

  public formListControlToGridMapper(
    control: FormControl,
    data: {
      gridItems: IBaseEntityType[];
      dependentData?: Record<string, IApiListResponse<IBaseEntityType>>;
    },
  ): IBaseEntityType[] {
    this.dependentData$.next(data.dependentData || {});

    // Filter event nodes that have warnings (at least one child site has manager issues)
    const eventsWithManagerIssues = filter(
      data.gridItems,
      (item) => item.hasWarning,
    );

    // If there are events with unavailable managers, show warning message and set form value
    if (!isEmpty(eventsWithManagerIssues)) {
      this.config.list.warningMessage$?.next(
        this.i18nService.instant(
          I18n.Forms.HawkerCommissaryAssignmentForm
            .HighlightedManagerNoLongerAvailable,
        ),
      );

      // Set form value and mark as dirty to trigger Save Changes button
      if (
        this.permissionService.canManage(
          this.config.entityNameToCheckPermissions,
        )
      ) {
        control.setValue(eventsWithManagerIssues);
        control.markAsDirty();
      }
    } else {
      this.config.list.warningMessage$.next('');
    }

    return data?.gridItems;
  }
}
